import type { inferRouterInputs } from '@sweetistics/trpc-server';
import { type MouseEvent, useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { Message } from '@/components/chat/chat-types';
import type { UniversalChatContextPayload } from '@/components/chat/universal-chat-context';
import { useUniversalChat } from '@/components/chat/universal-chat-context';
import type { SummaryDisplay } from '@/components/shared/link-preview/summary/types';
import { __getLinkPreviewContextMock, trpc } from '@/lib/trpc/client';
import type { AppRouter } from '@/types/app-router';
import { formatCharacterCountHint } from './character-hint';
import type { LinkMetadata, LinkPreviewContextData, SummaryState } from './types';
import { deriveDomain } from './utils';

interface UseLinkPreviewChatArguments {
  metadata: LinkMetadata | null | undefined;
  summaryDisplay: SummaryDisplay;
  summaryState: SummaryState;
  url: string;
}

type ChatAction = 'chat' | 'explain' | 'fact-check';

interface UseLinkPreviewChatResult {
  isChatEnabled: boolean;
  isChatLoading: boolean;
  startChatAction: (action: ChatAction, event?: MouseEvent<HTMLButtonElement>) => Promise<void>;
}

type LinkPreviewContextInput = inferRouterInputs<AppRouter>['utilities']['linkPreviewContext'];
type LinkPreviewChatMock = (
  args: UseLinkPreviewChatArguments,
  defaultResult: UseLinkPreviewChatResult
) => UseLinkPreviewChatResult;

let linkPreviewChatOverride: LinkPreviewChatMock | null = null;
let lastLinkPreviewChatResult: UseLinkPreviewChatResult | null = null;

export function __setLinkPreviewChatMock(mock: LinkPreviewChatMock) {
  linkPreviewChatOverride = mock;
}

export function __resetLinkPreviewChatMock() {
  linkPreviewChatOverride = null;
  lastLinkPreviewChatResult = null;
}

export function __getLastLinkPreviewChatResult(): UseLinkPreviewChatResult | null {
  return lastLinkPreviewChatResult;
}

const ACTION_PROGRESS_LABEL: Record<ChatAction, string> = {
  chat: 'Generating summary…',
  explain: 'Preparing explanation…',
  'fact-check': 'Running fact-check…',
};

const ACTION_USER_PROMPT: Record<ChatAction, string> = {
  chat: 'Summarize this link for me now.',
  explain: 'Explain this link in detail. Focus on the main ideas, why they matter, and how they connect.',
  'fact-check':
    'Fact-check the key claims in this link. Use search to verify them, cite sources with URLs, and call out anything questionable.',
};

const getActionProgressLabel = (action: ChatAction): string => {
  if (action === 'chat') {
    return ACTION_PROGRESS_LABEL.chat;
  }
  if (action === 'explain') {
    return ACTION_PROGRESS_LABEL.explain;
  }
  return ACTION_PROGRESS_LABEL['fact-check'];
};

const getActionUserPrompt = (action: ChatAction): string => {
  if (action === 'chat') {
    return ACTION_USER_PROMPT.chat;
  }
  if (action === 'explain') {
    return ACTION_USER_PROMPT.explain;
  }
  return ACTION_USER_PROMPT['fact-check'];
};

const buildInstructionLines = (action: ChatAction, isTranscript: boolean): string[] => {
  if (action === 'chat') {
    return [
      'You are assisting a user in summarizing the link content provided in the context above.',
      isTranscript
        ? 'The material is a transcript. Mention relevant speakers only if they are present in the context.'
        : 'The material is web page content. Focus on the central claims and supporting details.',
      'Write 3-4 concise bullet points highlighting the key ideas, noteworthy data, and any calls to action.',
      'Do not add disclaimers, speculation, or emojis.',
    ];
  }

  if (action === 'explain') {
    return [
      'You are helping a user deeply understand the link content provided in the context above.',
      'Break the material down into clear sections, explain how ideas connect, and translate any jargon into plain language.',
      'Highlight why the information matters and include brief examples or analogies when helpful.',
      'Keep the tone instructive and approachable; avoid speculation or filler.',
    ];
  }

  return [
    'You are fact-checking the link content provided in the context above.',
    'Identify major claims and verify them using the built-in search tool; cite credible sources with URLs for every verification.',
    'Flag statements that cannot be verified or that conflict with trustworthy sources, noting your confidence level.',
    'Maintain an objective, evidence-driven tone.',
  ];
};

export function useLinkPreviewChat({
  url,
  metadata,
  summaryDisplay,
  summaryState,
}: UseLinkPreviewChatArguments): UseLinkPreviewChatResult {
  const { openChat, registerContext, appendMessages, updateMessage, isEnabled } = useUniversalChat();
  const [isChatLoading, setIsChatLoading] = useState(false);
  const linkPreviewContextMutation = trpc.utilities.linkPreviewContext.useMutation();

  const startChatAction = useCallback(
    async (action: ChatAction, event?: MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (!isEnabled || isChatLoading) {
        return;
      }

      setIsChatLoading(true);

      const progressMessageId = `link-preview-progress-${Date.now()}`;
      const progressTimestamp = new Date();

      openChat();
      appendMessages([
        {
          id: progressMessageId,
          role: 'assistant',
          content: 'Loading link content…',
          timestamp: progressTimestamp,
          status: 'complete',
        },
      ]);

      try {
        const override = __getLinkPreviewContextMock();
        const payload = override
          ? await Promise.resolve(override({ url } as LinkPreviewContextInput))
          : await linkPreviewContextMutation.mutateAsync({ url } as LinkPreviewContextInput);

        const resolvedContent = typeof payload.content === 'string' ? payload.content : '';
        if (!resolvedContent.trim()) {
          throw new Error('Link did not return any readable content.');
        }

        const contextSource = `link-preview-${encodeURIComponent(url)}`;
        const resolvedTitle =
          metadata?.title ?? payload.title ?? (payload.url ? deriveDomain(payload.url) : deriveDomain(url));
        const nowIso = new Date().toISOString();

        const previewMetadata = metadata
          ? {
              title: metadata.title ?? null,
              description: metadata.description ?? null,
              siteName: metadata.siteName ?? null,
              image: metadata.image ?? null,
            }
          : null;

        const contextData: LinkPreviewContextData = {
          url: payload.url,
          title: payload.title ?? null,
          description: payload.description ?? null,
          siteName: payload.siteName ?? null,
          content: resolvedContent,
          truncated: payload.truncated,
          totalCharacters: payload.totalCharacters,
          wordCount: payload.wordCount,
          transcriptCharacters: payload.transcriptCharacters ?? null,
          transcriptLines: payload.transcriptLines ?? null,
          characterBudget: payload.characterBudget,
          snapshotCollectedAt: nowIso,
          previewMetadata,
        };

        const contextPayload: UniversalChatContextPayload = {
          source: contextSource,
          title: resolvedTitle,
          data: contextData,
          summary: summaryDisplay.summaryHasContent ? summaryState.text : undefined,
        };

        registerContext(contextPayload);

        const approximateCharacterCount =
          formatCharacterCountHint(payload.totalCharacters ?? payload.content?.length ?? null) ?? undefined;

        const progressActionLabel = getActionProgressLabel(action);
        const progressMessage = approximateCharacterCount
          ? `Link content ready (~${approximateCharacterCount} characters). ${progressActionLabel}`
          : `Link content ready. ${progressActionLabel}`;

        updateMessage({
          id: progressMessageId,
          role: 'assistant',
          content: progressMessage,
          timestamp: new Date(),
          status: 'complete',
        });

        const idBase = `${contextSource}-${Date.now()}`;
        const isTranscript = typeof payload.transcriptCharacters === 'number' && payload.transcriptCharacters > 0;

        const instructionLines = buildInstructionLines(action, isTranscript);

        const instructionMessage: Message = {
          id: `${idBase}-instructions`,
          role: 'system',
          content: instructionLines.join('\n'),
          timestamp: new Date(),
          visibility: 'hidden',
        };

        const summaryRequest: Message = {
          id: `${idBase}-request`,
          role: 'user',
          content: getActionUserPrompt(action),
          timestamp: new Date(),
          visibility: 'hidden',
        };

        appendMessages([instructionMessage, summaryRequest]);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to open chat for this link.';
        toast.error(message);
        updateMessage({
          id: progressMessageId,
          role: 'assistant',
          content: message,
          timestamp: new Date(),
          status: 'complete',
        });
      } finally {
        setIsChatLoading(false);
      }
    },
    [
      appendMessages,
      isChatLoading,
      isEnabled,
      metadata,
      openChat,
      registerContext,
      summaryDisplay.summaryHasContent,
      summaryState.text,
      linkPreviewContextMutation,
      updateMessage,
      url,
    ]
  );

  const baseResult: UseLinkPreviewChatResult = {
    isChatEnabled: isEnabled,
    isChatLoading,
    startChatAction,
  };

  const finalResult =
    linkPreviewChatOverride?.({ url, metadata, summaryDisplay, summaryState }, baseResult) ?? baseResult;

  lastLinkPreviewChatResult = finalResult;

  return finalResult;
}
